import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowRight, PoundSterling, ShieldCheck, BadgeCheck, Leaf, Home as HomeIcon } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { MEASURES, STEPS, IMAGES } from "../data/content";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO — Bento grid */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-24 bg-brand-cream relative overflow-hidden">
        <div className="absolute inset-0 grain-overlay" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
            {/* Headline block */}
            <Reveal className="md:col-span-7 bg-brand text-brand-cream rounded-3xl p-8 sm:p-12 flex flex-col justify-between min-h-[420px] relative overflow-hidden">
              <div className="absolute inset-0 grain-overlay" />
              <div className="relative">
                <p className="font-outfit text-xs uppercase tracking-[0.2em] text-brand-accent font-bold mb-6 flex items-center gap-2">
                  <Leaf className="w-4 h-4" /> Network Survey Limited
                </p>
                <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
                  Warmer homes,<br />lower bills —<br />
                  <span className="text-brand-accent">zero upfront cost.</span>
                </h1>
                <p className="font-figtree text-lg text-brand-cream/75 mt-6 max-w-lg">
                  Insulation, boilers, solar PV and heat pumps installed and funded through government grants and flexible finance.
                </p>
              </div>
              <div className="relative flex flex-wrap gap-3 mt-8">
                <Link to="/contact" data-testid="hero-cta-button" className="bg-brand-accent text-white hover:bg-brand-accenthover transition-colors rounded-full px-7 py-3.5 font-medium inline-flex items-center gap-2">
                  Get your free survey <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/measures" data-testid="hero-measures-button" className="border-2 border-brand-cream/30 text-brand-cream hover:bg-brand-cream/10 transition-colors rounded-full px-7 py-3.5 font-medium">
                  Explore measures
                </Link>
              </div>
            </Reveal>

            {/* Image block */}
            <Reveal delay={0.1} className="md:col-span-5 rounded-3xl overflow-hidden min-h-[420px] relative">
              <img src={IMAGES.hero} alt="Home with solar panels" className="w-full h-full object-cover absolute inset-0" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand/70 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 backdrop-blur-md bg-white/15 border border-white/20 rounded-2xl p-5">
                <p className="font-outfit text-white text-2xl font-bold">£7,500</p>
                <p className="font-figtree text-white/90 text-sm">grant available toward a heat pump*</p>
              </div>
            </Reveal>

            {/* Stat tiles */}
            <Reveal delay={0.15} className="md:col-span-4 bg-white border border-stone-200 rounded-3xl p-7 flex flex-col justify-center">
              <PoundSterling className="w-8 h-8 text-brand-accent mb-3" />
              <p className="font-outfit text-3xl font-bold text-brand">No deposit</p>
              <p className="font-figtree text-stone-600 text-sm mt-1">Pay nothing upfront — funded by grants & finance.</p>
            </Reveal>
            <Reveal delay={0.2} className="md:col-span-4 bg-white border border-stone-200 rounded-3xl p-7 flex flex-col justify-center">
              <ShieldCheck className="w-8 h-8 text-brand-accent mb-3" />
              <p className="font-outfit text-3xl font-bold text-brand">Accredited</p>
              <p className="font-figtree text-stone-600 text-sm mt-1">MCS, Gas Safe & TrustMark installers only.</p>
            </Reveal>
            <Reveal delay={0.25} className="md:col-span-4 bg-brand-accent text-white rounded-3xl p-7 flex flex-col justify-center">
              <BadgeCheck className="w-8 h-8 mb-3" />
              <p className="font-outfit text-3xl font-bold">Free survey</p>
              <p className="font-figtree text-white/90 text-sm mt-1">No-obligation home assessment & tailored plan.</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Ribbon */}
      <div className="bg-brand-dark py-4 border-y border-brand-success/30">
        <Marquee speed={40} gradient={false} autoFill>
          {["Insulation", "New Boilers", "Solar PV", "Heat Pumps", "ECO4 Scheme", "Boiler Upgrade Scheme", "Great British Insulation Scheme", "No Upfront Cost"].map((t, i) => (
            <span key={i} className="font-outfit uppercase tracking-[0.2em] text-xs text-brand-cream/60 mx-8 flex items-center gap-8">
              {t} <span className="text-brand-accent">✦</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* MEASURES */}
      <section className="py-20 md:py-28 bg-brand-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-outfit text-xs uppercase tracking-[0.2em] text-brand-accent font-bold mb-3">What we install</p>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
              <h2 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-brand max-w-xl">Energy-saving measures, fully managed</h2>
              <Link to="/measures" className="text-brand-accent font-medium inline-flex items-center gap-2 hover:gap-3 transition-all">View all measures <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {MEASURES.map((m, i) => (
              <Reveal key={m.slug} delay={i * 0.08}>
                <Link to="/measures" data-testid={`measure-card-${m.slug}`} className="group block bg-white border border-stone-200 rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-300 h-full">
                  <div className="h-44 overflow-hidden">
                    <img src={m.image} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-6">
                    <div className="w-11 h-11 rounded-xl bg-brand-cream flex items-center justify-center mb-4 -mt-12 relative border border-stone-200">
                      <m.icon className="w-5 h-5 text-brand-accent" />
                    </div>
                    <h3 className="font-outfit text-xl font-medium text-brand mb-1">{m.name}</h3>
                    <p className="font-figtree text-sm text-stone-600">{m.tagline}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NO UPFRONT COST split */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal className="rounded-3xl overflow-hidden">
            <img src={IMAGES.family} alt="Family at home" className="w-full h-[460px] object-cover" />
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-outfit text-xs uppercase tracking-[0.2em] text-brand-accent font-bold mb-3">No upfront costs</p>
            <h2 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-brand mb-5">Upgrade your home without the upfront bill</h2>
            <p className="font-figtree text-stone-600 text-lg mb-8">
              We believe a warmer, more efficient home shouldn't depend on having thousands in the bank. That's why we structure every installation around the funding available to you.
            </p>
            <ul className="space-y-4">
              {[
                ["Government grants", "We check your eligibility for schemes like the Boiler Upgrade Scheme, ECO4 and more."],
                ["Flexible finance", "Where a grant doesn't cover everything, spread the cost in affordable monthly payments."],
                ["Subscription-style installs", "Low monthly plans mean no large deposit and predictable costs."],
              ].map(([t, d]) => (
                <li key={t} className="flex gap-4">
                  <span className="mt-1 w-6 h-6 rounded-full bg-brand-success/15 flex items-center justify-center shrink-0">
                    <BadgeCheck className="w-4 h-4 text-brand-success" />
                  </span>
                  <div>
                    <p className="font-outfit font-medium text-brand">{t}</p>
                    <p className="font-figtree text-sm text-stone-600">{d}</p>
                  </div>
                </li>
              ))}
            </ul>
            <Link to="/grants-finance" data-testid="upfront-grants-link" className="mt-8 inline-flex items-center gap-2 bg-brand text-white hover:bg-brand-dark transition-colors rounded-full px-7 py-3.5 font-medium">
              How funding works <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-28 bg-brand-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-outfit text-xs uppercase tracking-[0.2em] text-brand-accent font-bold mb-3">How it works</p>
            <h2 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-brand mb-12 max-w-xl">From survey to switch-on in four simple steps</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="bg-white border border-stone-200 rounded-2xl p-7 h-full">
                  <span className="font-outfit text-4xl font-bold text-brand-accent/30">{s.n}</span>
                  <h3 className="font-outfit text-xl font-medium text-brand mt-3 mb-2">{s.title}</h3>
                  <p className="font-figtree text-sm text-stone-600">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-brand relative overflow-hidden">
        <div className="absolute inset-0 grain-overlay" />
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative">
          <Reveal>
            <HomeIcon className="w-10 h-10 text-brand-accent mx-auto mb-6" />
            <h2 className="font-outfit text-3xl sm:text-5xl font-bold tracking-tight text-brand-cream mb-5">Ready to cut your energy bills?</h2>
            <p className="font-figtree text-lg text-brand-cream/70 mb-9 max-w-xl mx-auto">Book your free, no-obligation survey today and find out exactly what funding you qualify for.</p>
            <Link to="/contact" data-testid="bottom-cta-button" className="bg-brand-accent text-white hover:bg-brand-accenthover transition-colors rounded-full px-9 py-4 font-medium inline-flex items-center gap-2 text-lg">
              Request my free survey <ArrowRight className="w-5 h-5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
